import { useEffect, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { Button, Modal, ProgressBar } from 'react-bootstrap';

interface CloudServerTransferModalProps {
  show: boolean;
  totalBytes: number;
  onCancel: () => void;
}

// Simulated server-to-server throughput. We can't measure the real
// provider→server transfer from the browser, so we estimate progress from the
// file size at this rate. Kept deliberately conservative so the bar doesn't
// race ahead of the actual transfer — it's capped at MAX_SIMULATED_PERCENT and
// only completes when the real request resolves (the modal closes).
// Calibrated from a real sample: ~530 MB took ~40 s server-to-server (~13 MB/s).
const ASSUMED_BYTES_PER_SECOND = 13 * 1024 * 1024; // 13 MB/s
const MAX_SIMULATED_PERCENT = 90;
const TICK_MS = 200;
const MIN_ESTIMATED_MS = 1500;

const formatBytes = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unitIndex]}`;
};

const CloudServerTransferModal = ({
  show,
  totalBytes,
  onCancel
}: CloudServerTransferModalProps) => {
  const { t } = useTranslate();
  const [percent, setPercent] = useState(0);
  const formattedSize = formatBytes(totalBytes);

  useEffect(() => {
    if (!show) {
      setPercent(0);
      return;
    }

    const estimatedMs = Math.max(
      MIN_ESTIMATED_MS,
      (totalBytes / ASSUMED_BYTES_PER_SECOND) * 1000
    );
    const startedAt = Date.now();
    setPercent(0);

    const interval = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const next = Math.min(
        MAX_SIMULATED_PERCENT,
        (elapsed / estimatedMs) * 100
      );
      setPercent(next);
    }, TICK_MS);

    return () => clearInterval(interval);
  }, [show, totalBytes]);

  return (
    <Modal show={show} backdrop="static" centered>
      <Modal.Body>
        <div className="d-flex flex-column align-items-center text-center gap-3 py-3">
          <div>
            <div className="fw-bold mb-1">
              {t('lomavisCreator.additionalOptions.serverTransferTitle')}
            </div>
            <div className="text-muted small">
              {t('lomavisCreator.additionalOptions.serverTransferBody')}
            </div>
          </div>

          <div className="w-100 px-3">
            <ProgressBar
              now={percent}
              variant="primary"
              animated
              label={`${Math.round(percent)}%`}
              style={{ height: '16px' }}
            />
            {formattedSize && (
              <div className="text-muted small mt-2 fw-bold">
                {t('lomavisCreator.additionalOptions.serverTransferSize', {
                  size: formattedSize
                })}
              </div>
            )}
          </div>

          <Button variant="outline-secondary" size="sm" onClick={onCancel}>
            {t('lomavisCreator.buttons.cancel')}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CloudServerTransferModal;
